import { format, parseISO, isValid } from 'date-fns';
import { ru } from 'date-fns/locale';

// Number formatting
export const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined) return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return '-';
  return num.toLocaleString('ru-RU', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const formatCurrency = (value, currency = 'USD', compact = false) => {
  if (value === null || value === undefined) return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return '-';

  const options = {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: compact ? 0 : 2,
  };

  if (compact && Math.abs(num) >= 1000000) {
    return (num / 1000000).toLocaleString('ru-RU', { ...options, style: 'decimal' }) + 'M ' + currency;
  }
  if (compact && Math.abs(num) >= 1000) {
    return (num / 1000).toLocaleString('ru-RU', { ...options, style: 'decimal' }) + 'K ' + currency;
  }

  return num.toLocaleString('ru-RU', options);
};

export const formatPercent = (value, decimals = 2) => {
  if (value === null || value === undefined) return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return '-';
  return num.toLocaleString('ru-RU', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }) + '%';
};

// Date formatting
export const formatDate = (dateString, formatStr = 'dd.MM.yyyy') => {
  if (!dateString) return '-';
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
    if (!isValid(date)) return '-';
    return format(date, formatStr, { locale: ru });
  } catch {
    return '-';
  }
};

export const formatDateTime = (dateString) => {
  return formatDate(dateString, 'dd.MM.yyyy HH:mm');
};

export const formatRelativeDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
    if (!isValid(date)) return '-';

    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return `${diffInMinutes} мин. назад`;
    }
    if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} ч. назад`;
    }
    if (diffInHours < 48) {
      return 'Вчера';
    }
    return formatDate(dateString);
  } catch {
    return '-';
  }
};

// Asset type labels
export const ASSET_TYPES = {
  EQUITY: 'Акции',
  BONDS: 'Облигации',
  CASH: 'Денежные средства',
  DEPOSIT: 'Депозиты',
  PROPERTY: 'Недвижимость',
  PRIVATE_EQUITY: 'Private Equity',
  ETF: 'ETF',
  FUND: 'Фонды',
};

export const getAssetTypeLabel = (type) => ASSET_TYPES[type] || type;

// Operation type labels
export const OPERATION_TYPES = {
  BUY: 'Покупка',
  SELL: 'Продажа',
  DEPOSIT: 'Пополнение',
  WITHDRAWAL: 'Вывод',
  DIVIDEND: 'Дивиденды',
  UNLOCK: 'Разблокировка',
  BLOCK: 'Блокировка',
  TRANSFER: 'Перевод',
};

export const getOperationTypeLabel = (type) => OPERATION_TYPES[type] || type;

// Status labels
export const STATUS_LABELS = {
  Free: 'Свободный',
  Blocked: 'Заблокирован',
  Pending: 'Ожидание',
  Completed: 'Выполнено',
  Rejected: 'Отклонено',
};

export const getStatusLabel = (status) => STATUS_LABELS[status] || status;

// Country labels
export const COUNTRY_LABELS = {
  RU: 'Россия',
  US: 'США',
  UAE: 'ОАЭ',
  CY: 'Кипр',
  Other: 'Другое',
};

export const getCountryLabel = (country) => COUNTRY_LABELS[country] || country;

// Holder type labels
export const HOLDER_TYPES = {
  INDIVIDUAL: 'Физ. лицо',
  COMPANY: 'Компания',
  TRUST: 'Траст',
};

export const getHolderTypeLabel = (type) => HOLDER_TYPES[type] || type;

// Color helpers
export const getProfitLossColor = (value) => {
  const num = parseFloat(value);
  if (num > 0) return 'success.main';
  if (num < 0) return 'error.main';
  return 'text.secondary';
};

export const getStatusColor = (status) => {
  switch (status) {
    case 'Free':
    case 'Completed':
      return 'success';
    case 'Blocked':
    case 'Rejected':
      return 'error';
    case 'Pending':
      return 'warning';
    default:
      return 'default';
  }
};

// Asset type colors for charts
export const ASSET_TYPE_COLORS = {
  EQUITY: '#3498DB',
  BONDS: '#9B59B6',
  CASH: '#27AE60',
  DEPOSIT: '#2ECC71',
  PROPERTY: '#E67E22',
  PRIVATE_EQUITY: '#1ABC9C',
  ETF: '#F39C12',
  FUND: '#E74C3C',
};

export const getAssetTypeColor = (type) => ASSET_TYPE_COLORS[type] || '#95A5A6';
